import Vue from 'vue'
import Router from 'vue-router'
import store from './../store/index'
Vue.use(Router)

// Helper function to check if user has any connected pages (Helps determine if onboarding is needed for users)
async function checkIfUserHasPages() {
  try {
    const response = await Vue.http.get(
      `${process.env.VUE_APP_API_HOST}/pages`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    )

    if (response.ok && response.body && response.body.pages) {
      return response.body.pages.length > 0
    }

    return false
  } catch (error) {
    console.error('[Router Guard] Error checking pages:', error)
    // On error, assume user has pages to avoid blocking access
    return true
  }
}

const queryParams = new URLSearchParams(window.location.search)
if (queryParams.get('supervisortoken')) {
  window.$crisp.push(['do', 'session:reset'])
  localStorage.clear()
  localStorage.setItem('token', queryParams.get('supervisortoken'))
  localStorage.setItem('supervisor', true)
  if (queryParams.get('viewer')) window.location = '/#/dash/planner'
  else window.location = '/'
}

if (
  !localStorage.token &&
  ['post_id', 'page_id'].every(p => queryParams.has(p))
)
  localStorage.setItem('redirect_params_url', window.location.href)

const router = new Router({
  routes: [
    {
      name: 'underscore',
      path: '/_',
      redirect: '/dash/overview',
      component: () => import('@/views/Dash'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })
        next()
      }
    },
    {
      name: 'dash',
      path: '/dash',
      redirect: '/dash/overview',
      component: () => import('@/views/Dash'),
      beforeEnter: async (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // const InProduction = true // Enabled for all environments (remove to restrict to local/sandbox only)
        const InProduction = process.env.NODE_ENV !== 'production'

        const onboardingCompleted = localStorage.getItem('onboardingCompleted') === 'true'
        const onboardingSkipped = localStorage.getItem('onboardingSkipped') === 'true'

        // If user is mid-onboarding (onboarding_mode flag set by StepConnectPages before OAuth
        // redirect), bypass the page/payment checks entirely and go back to onboarding.
        // Without this, OAuth callbacks that land at the root URL get routed through /dash,
        // which either redirects to /onboarding/networks (no pages yet) or, worse, sets
        // onboardingCompleted='true' prematurely (user already has pages from a previous
        // network), both of which break the onboarding flow.
        if (localStorage.getItem('onboarding_mode') === 'true' && !onboardingCompleted && !onboardingSkipped) {
          let isAdmin = false
          try {
            const raw = localStorage.getItem('user')
            const u = raw && raw !== 'undefined' ? JSON.parse(raw) : null
            isAdmin = !!(u && u.admin)
          } catch (e) { /* ignore */ }

          if (isAdmin) {
            const isSummaryRefresh = localStorage.getItem('onboarding_summary_refresh') === 'true'
            // OAuth params land in window.location.search (before the hash) in hash-mode routing
            const searchParams = new URLSearchParams(window.location.search)
            const query = Object.fromEntries(searchParams)
            console.log('[Router Guard /dash] Onboarding mode active — bypassing dash guard, redirecting to onboarding', { isSummaryRefresh, query })
            return next(
              isSummaryRefresh
                ? { name: 'onboarding-summary', query }
                : { name: 'onboarding-connectPages', query }
            )
          }
        }

        // If onboarding already done or skipped, check payment regulation then allow dash
        if (onboardingCompleted || onboardingSkipped) {
          if (InProduction) {
            try {
              const userStr = localStorage.getItem('user')
              if (userStr && userStr !== 'undefined') {
                const user = JSON.parse(userStr)
                if (user && user._premium && !user._premium.active) {
                  if (user.admin) {
                    console.log('[Router Guard] Subscription inactive (admin) - redirecting to payment-regulation')
                    return next({ name: 'payment-regulation' })
                  } else {
                    console.log('[Router Guard] Subscription inactive (non-admin) - redirecting to payment-blocked')
                    return next({ name: 'payment-blocked' })
                  }
                }
              }
            } catch (e) {
              console.error('[Router Guard] Error checking payment status:', e)
            }
          }
          return next()
        }

        if (InProduction) {
          let user = null
          try {
            const raw = localStorage.getItem('user')
            if (raw && raw !== 'undefined') user = JSON.parse(raw)
          } catch (e) { /* ignore */ }

          const hasActivePlan = user && user._premium && user._premium.active
          const hasPages = await checkIfUserHasPages()

          if (!hasActivePlan && hasPages) {
            console.log('[Router Guard] Subscription lapsed - user has pages but no active plan - redirecting to payment page')
            if (user && user.admin) {
              return next({ name: 'payment-regulation' })
            } else {
              return next({ name: 'payment-blocked' })
            }
          }

          // Non-admin users are never redirected to /onboarding — mark as completed and proceed
          if (!user || !user.admin) {
            localStorage.setItem('onboardingCompleted', 'true')
            return next()
          }

          if (!hasActivePlan) {
            console.log('[Router Guard] User has no active plan - redirecting to onboarding/needs')
            return next({ path: '/onboarding/needs' })
          }

          if (!hasPages) {
            console.log('[Router Guard] User has plan but no pages - redirecting to onboarding/networks')
            return next({ path: '/onboarding/networks' })
          }

          // Active plan + pages: auto-mark onboarding as completed
          // so we skip these checks on subsequent navigations
          localStorage.setItem('onboardingCompleted', 'true')
        }

        next()
      },
      children: [
        {
          path: 'overview',
          name: 'overview',
          component: () => import('@/components/dash/section/Overview'),
          beforeEnter: (to, from, next) => {
            // Intercept OAuth returns in onboarding mode
            const isOnboardingMode = localStorage.getItem('onboarding_mode')
            const isSummaryRefresh = localStorage.getItem('onboarding_summary_refresh')
            const currentNetwork = localStorage.getItem('onboarding_current_network')
            const currentIndex = localStorage.getItem('onboarding_current_index')

            // Check for OAuth callback params
            const queryParams = new URLSearchParams(window.location.search)
            const hasOAuthParams =
              queryParams.has('code') ||
              queryParams.has('oauth_token') ||
              queryParams.has('oauth_verifier')

            const onboardingDone =
              localStorage.getItem('onboardingCompleted') === 'true' ||
              localStorage.getItem('onboardingSkipped') === 'true'

            if (isOnboardingMode === 'true' && hasOAuthParams && !onboardingDone) {
              // Only redirect admin users to onboarding — clear the flag and proceed for non-admins
              let isAdmin = false
              try {
                const raw = localStorage.getItem('user')
                const u = raw && raw !== 'undefined' ? JSON.parse(raw) : null
                isAdmin = !!(u && u.admin)
              } catch (e) { /* ignore */ }

              if (!isAdmin) {
                localStorage.removeItem('onboarding_mode')
              } else if (isSummaryRefresh === 'true') {
                console.log('[Router Guard] Redirecting to onboarding-summary (refresh mode)')
                return next({
                  name: 'onboarding-summary',
                  query: to.query
                })
              } else {
                console.log('[Router Guard] Redirecting to onboarding-connectPages')
                return next({
                  name: 'onboarding-connectPages',
                  query: to.query
                })
              }
            }
            next()
          }
        },
        {
          path: 'pages',
          name: 'pages',
          component: () => import('@/components/dash/section/Pages'),
          beforeEnter: (to, from, next) => {
            // Intercept OAuth returns in onboarding mode BEFORE component mounts
            const isOnboardingMode = localStorage.getItem('onboarding_mode')
            const isSummaryRefresh = localStorage.getItem('onboarding_summary_refresh')
            const onboardingDone =
              localStorage.getItem('onboardingCompleted') === 'true' ||
              localStorage.getItem('onboardingSkipped') === 'true'

            if (isOnboardingMode === 'true' && !onboardingDone) {
              // Only redirect admin users to onboarding — clear the flag and proceed for non-admins
              let isAdmin = false
              try {
                const raw = localStorage.getItem('user')
                const u = raw && raw !== 'undefined' ? JSON.parse(raw) : null
                isAdmin = !!(u && u.admin)
              } catch (e) { /* ignore */ }

              if (!isAdmin) {
                localStorage.removeItem('onboarding_mode')
                // Proceed normally to the Pages component
              } else if (isSummaryRefresh === 'true') {
                console.log('[Router Guard] Summary refresh detected - redirecting to onboarding-summary')
                console.log('[Router Guard] Preserving query params:', to.query)
                return next({
                  name: 'onboarding-summary',
                  query: to.query
                })
              } else {
                console.log('[Router Guard] Onboarding mode detected - redirecting to onboarding-connectPages')
                console.log('[Router Guard] Preserving query params:', to.query)
                console.log('[Router Guard] OAuth callback will be handled in StepConnectPages')
                // Do not remove the flag here, StepConnectPages will handle it
                return next({
                  name: 'onboarding-connectPages',
                  query: to.query
                })
              }
            }
            console.log('[Router Guard] Normal mode - proceeding to Pages component')
            next()
          }
        },
        {
          path: 'bots',
          name: 'bots',
          component: () => import('@/components/dash/section/Bots'),
          props: true,
          alias: ['bots/new', 'bots/edit']
        },
        {
          path: 'posts',
          name: 'posts',
          beforeEnter: (to, from, next) => {
            const version = localStorage.getItem('version_')
            if (
              version !== 'old' &&
              !('no_redirection' in to.query || 'no_redirection' in to.params)
            ) {
              next('/dash/posts2')
            } else {
              next()
            }
          },
          component: () => import('@/components/dash/section/Posts'),
          props: true
        },
        {
          path: 'planner',
          name: 'planner',
          beforeEnter: (to, from, next) => {
            const version = localStorage.getItem('version_')
            if (
              version !== 'old' &&
              !('no_redirection' in to.query || 'no_redirection' in to.params)
            ) {
              next('/dash/posts2')
            } else {
              next()
            }
          },
          component: () => import('@/components/dash/section/Posts'), // Affiche ce composant si aucune redirection
          props: route => ({
            date: route.params.date || route.query.date || null
          })
        },

        {
          path: 'moderation',
          name: 'moderation',
          component: () => import('@/components/dash/section/Moderation'),
          props: true
        },
        {
          path: 'settings/:tab?',
          name: 'settings',
          component: () => import('@/components/dash/section/Settings'),
          alias: ['settings/users', 'settings/user']
        },
        {
          path: 'invoice',
          name: 'invoice',
          component: () => import('@/components/dash/section/Invoice')
        },
        {
          path: 'sources',
          name: 'sources',
          component: () => import('@/components/dash/section/Sources')
        },
        {
          path: 'reporting',
          name: 'reporting',
          component: () => import('@/components/dash/section/Reporting')
        },
        {
          path: 'plan',
          name: 'plan',
          component: () => {
            // Plan2 in local/sandbox for tests
            // const isLocalOrSandbox =
            //   process.env.NODE_ENV !== 'production' ||
            //   window.location.href.includes('sandbox')
            // return isLocalOrSandbox
            //   ? import('@/components/dash/section/Plan2')
            //   : import('@/components/dash/section/Plan')
            return import('@/components/dash/section/Plan2')
          }
        },
        
        {
          path: 'postCreator',
          name: 'postCreator',
          component: () => import('@/components/dash/section/PostCreator'),
          props: {
            mode: 'create'
          } // Définit le mode par défaut comme 'create'
        },
        {
          path: 'postCreator/:mode',
          name: 'postCreator',
          component: () => import('@/components/dash/section/PostCreator'),
          props: true
        },
        {
          path: 'posts2',
          name: 'posts2',
          beforeEnter: (to, from, next) => {
            const version = localStorage.getItem('version_')
            if (
              version == 'old' &&
              !('no_redirection' in to.query || 'no_redirection' in to.params)
            ) {
              next('/dash/planner')
            } else {
              next()
            }
          },
          component: () => import('@/components/dash/section/Posts2'),
          props: route => {
            console.log('props posts2', route)
            return {
              ...route.query,
              ...route.params
            }
          }
        }
      ]
    },
    {
      name: 'auth',
      path: '/',
      component: () => import('@/views/Auth'),
      beforeEnter: (_to, _from, next) => {
        if (localStorage.token) {
          //lien profond, (temporaire)
          const version = localStorage.getItem('version_')
          if (version !== 'old') {
            const queryParams = Object.fromEntries(
              new URLSearchParams(window.location.search)
            )
            if (
              queryParams.post_id &&
              queryParams.page_id &&
              queryParams.bot_id &&
              !queryParams.no_redirection
            ) {
              location.href = `/#/dash/posts2${window.location.search}`
              return
            }
          }

          return next({ name: 'dash' })
        }
        next()
      },
      children: [
        {
          path: 'confirm',
          name: 'confirm'
        }
      ]
    },
    {
      name: 'onboarding',
      path: '/onboarding',
      component: () => import('@/views/Onboarding'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // Onboarding is restricted to Admin users only
        try {
          const raw = localStorage.getItem('user')
          const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null
          if (!user || !user.admin) return next({ name: 'dash' })
        } catch (e) {
          return next({ name: 'dash' })
        }

        next()
      },
      children: [
        {
          path: '',
          redirect: 'needs'
        },
        {
          path: 'needs',
          name: 'onboarding-needs',
          component: () => import('@/components/onboarding/steps/StepNeeds')
        },
        {
          path: 'networks',
          name: 'onboarding-networks',
          component: () => import('@/components/onboarding/steps/StepNetworks')
        },
        {
          path: 'features',
          name: 'onboarding-features',
          component: () => import('@/components/onboarding/steps/StepFeatures')
        },
        {
          path: 'connectPages',
          name: 'onboarding-connectPages',
          component: () =>
            import('@/components/onboarding/steps/StepConnectPages')
        },
        {
          path: 'connectNatives',
          name: 'onboarding-connectNatives',
          component: () =>
            import('@/components/onboarding/steps/StepConnectNatives')
        },
        {
          path: 'connectExtensions',
          name: 'onboarding-connectExtensions',
          component: () =>
            import('@/components/onboarding/steps/StepConnectExtensions')
        },
        {
          path: 'summary',
          name: 'onboarding-summary',
          component: () =>
            import('@/components/onboarding/steps/StepSummary')
        },
        {
          path: 'payment',
          name: 'onboarding-payment',
          component: () =>
            import('@/components/onboarding/steps/StepPayment')
        }
      ]
    },
    {
      name: 'payment-regulation',
      path: '/payment-regulation',
      component: () => import('@/views/PaymentRegulation'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // Payment regulation is restricted to Admin users only
        try {
          const raw = localStorage.getItem('user')
          const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null
          if (!user || !user.admin) return next({ name: 'dash' })
        } catch (e) {
          return next({ name: 'dash' })
        }

        next()
      }
    },
    {
      name: 'payment-blocked',
      path: '/payment-blocked',
      component: () => import('@/views/PaymentBlocked'),
      beforeEnter: (_to, _from, next) => {
        if (!localStorage.token) return next({ name: 'auth' })

        // Only non-admin users are redirected here — admins go to /payment-regulation
        try {
          const raw = localStorage.getItem('user')
          const user = raw && raw !== 'undefined' ? JSON.parse(raw) : null
          if (user && user.admin) return next({ name: 'payment-regulation' })
        } catch (e) {
          return next({ name: 'dash' })
        }

        next()
      }
    },
    {
      name: 'invite',
      path: '/invite',
      redirect: '/',
      component: () => import('@/views/Invite'),
      children: [
        {
          path: 'shared-planner',
          name: 'shared-planner',
          component: () => import('@/components/dash/section/SharedPlanner')
        },
        {
          path: 'viewer',
          name: 'viewer',
          component: () => import('@/components/dash/section/ViewerLogin')
        }
      ]
    },
    {
      name: 'legal',
      path: '/',
      redirect: '/',
      component: () => import('@/views/Invite'),
      children: [
        {
          path: 'legal',
          name: 'legal',
          component: () => import('@/components/dash/section/Legal')
        }
      ]
    },
    {
      name: 'preview',
      path: '/preview',
      component: () => import('@/views/Preview'),
      children: [
        {
          path: 'story/:idStorySelected?',
          name: 'story',
          component: () => import('@/components/preview/Story'),
          props: route => ({
            idStorySelected: route.params.idStorySelected,
            preview: route.query.preview === 'true' // ?preview=true
          })
        },

        {
          path: 'post/:pageIdSelected?',
          name: 'post',
          component: () => import('@/components/preview/Post'),
          props: route => ({ pageIdSelected: route.params.pageIdSelected })
        }
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  // Check if returning from extension (onboarding mode)
  const extensionReturnStr = localStorage.getItem('mashup_extension_return')

  if (extensionReturnStr && to.name !== 'onboarding-connectExtensions') {
    try {
      const returnData = JSON.parse(extensionReturnStr)
      const age = Date.now() - returnData.timestamp

      // If data is recent (less than 30 seconds) and we're not already going to extensions
      if (age < 30000 && returnData.success) {
        console.log('[Router Guard] Extension return detected, redirecting to onboarding-connectExtensions')
        return next({ name: 'onboarding-connectExtensions' })
      } else if (age >= 30000) {
        // Clean up old data
        console.log('[Router Guard] Extension return data too old, cleaning up')
        localStorage.removeItem('mashup_extension_return')
      }
    } catch (error) {
      console.error('[Router Guard] Error parsing extension return:', error)
      localStorage.removeItem('mashup_extension_return')
    }
  }

  // Block all navigation when subscription is inactive (only for users who completed onboarding,
  // to avoid blocking new users who don't have a plan yet mid-onboarding)
  const inProduction = process.env.NODE_ENV !== 'production'
  // const inProduction = true // Enabled for all environments (remove to restrict to local/sandbox only)
  const onboardingDone =
    localStorage.getItem('onboardingCompleted') === 'true' ||
    localStorage.getItem('onboardingSkipped') === 'true'
  if (inProduction && onboardingDone && to.name !== 'payment-blocked' && to.name !== 'payment-regulation' && to.name !== 'auth') {
    try {
      const raw = localStorage.getItem('user')
      if (raw && raw !== 'undefined') {
        const user = JSON.parse(raw)
        if (user && user._premium && !user._premium.active) {
          if (user.admin) {
            return next({ name: 'payment-regulation' })
          } else {
            return next({ name: 'payment-blocked' })
          }
        }
      }
    } catch (e) { /* ignore */ }
  }

  store.dispatch('selectPage/fetchData')
  store.commit('selectPage/loadPagesSelectedFromLocalStorage')
  next()
})

export default router
